<template>
	<Panel
		:title="state ? state : 'Enter Notary Information'"
		class="notary-state-info-panel"
		:action="notaryActions"
	>
		<fieldset
			id="notary-state-fieldset"
			:disabled="!isEditingNotary"
		>
			<InputTextbox
				label="Enter Notary Registration Number"
				type="text"
				v-model:saturn="commissionNumber"
				placeholder="Notary Registration Number"
				:invalid="v$.commissionNumber.required.$invalid"
			>
				<template #validation>
					<div v-if="v$.commissionNumber.required.$invalid">Notary Registration Number Required</div>
				</template>
			</InputTextbox>
			<InputTextbox
				v-if="isEditingNotary"
				label="Re-enter Notary Registration Number"
				type="text"
				v-model:saturn="commissionNumberConfirm"
				placeholder="Re-enter Notary Registration Number"
				:invalid="v$.commissionNumberConfirm.required.$invalid || v$.commissionNumberConfirm.sameAs.$invalid"
			>
				<template #validation>
					<div v-if="v$.commissionNumberConfirm.required.$invalid">Re-enter Notary Registration Number</div>
					<div v-else-if="v$.commissionNumberConfirm.sameAs.$invalid">Notary Registration Numbers must match</div>
				</template>
			</InputTextbox>
			<div :style="$grid('2-2')">
				<InputDate
					v-model:saturn="expirationDate"
					placeholder="Expiration Date"
					label="Expiration Date"
					:invalid="v$.expirationDate.required.$invalid || v$.expirationDate.dateInFuture.$invalid"
				>
					<template #validation>
						<div v-if="v$.expirationDate.required.$invalid">Expiration date is required</div>
						<div v-else-if="v$.expirationDate.dateInFuture.$invalid">Expiration date must be in the future</div>
					</template>
				</InputDate>
				<InputTextbox
					label="County"
					type="text"
					v-model:saturn="county"
					placeholder="County"
					:invalid="v$.county.required.$invalid || v$.county.noNumbers.$invalid"
				>
					<template #validation>
						<div v-if="v$.county.required.$invalid">County is required</div>
						<div v-if="v$.county.noNumbers.$invalid">County can not contain numbers</div>
					</template>
				</InputTextbox>
				<InputRichDropdown
					label="State"
					v-model:saturn="state"
					:list="ENUMS.states"
					:valueMap="(state: any) => state.code"
					:display="(state: any) => state.description"
					:invalid="v$.state.required.$invalid || v$.state.stateAlreadyExists.$invalid"
					@change="stateDropDownChangeHandler"
					search
				>
					<template #validation>
						<div v-if="v$.state.required.$invalid">State is required</div>
						<div v-else-if="v$.state.stateAlreadyExists.$invalid">Notary Setting with this state already exists</div>
					</template>
				</InputRichDropdown>
			</div>
		</fieldset>
		<NotaryStampPreview
			ref="notaryStampPreviewRef"
			@notaryStampImgGenerated="saveNotaryStampImg"
			:notarySettings="notarySettings"
			:notaryStateSetting="notaryStateSetting"
			:commissionNumber="commissionNumber"
			:commissionExpirationDate="expirationDate"
			:notaryCounty="county"
			:employeeName="fullName"
		/>
	</Panel>
</template>
<script lang="ts">
	export interface INotaryUserInfoProps {
		fullName: string;
		isNotary: boolean;
		notarySettings?: INotaryStateSetting[];
		notaryInfo?: IUserNotaryInformation;
		userNotaryInformation?: IUserNotaryInformation[];
	}
</script>
<script setup lang="ts">
	import { IUserNotaryInformation, INotaryStateSetting } from '@core/classes/Notary/NotaryInformation';

	import InputDate from '@core/components/InputDate.vue';
	import InputRichDropdown from '@core/components/InputRichDropdown.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import modalInfo from '@core/modals/modalInfo.vue';
	import NotaryStampPreview from '@core/components/NotaryStampPreview.vue';
	import Panel from '@core/components/Panel.vue';

	import { required, sameAs } from '@vuelidate/validators';
	import $modal from '@core/services/modal';
	import { dateInFuture } from '@core/services/custom-validators';
	import ENUMS from '@core/classes/Enums';
	import { useVuelidate } from '@vuelidate/core';

	import { computed, ref, onMounted, useTemplateRef } from 'vue';

	const props = defineProps<INotaryUserInfoProps>();
	const emit = defineEmits<{
		(e: 'notaryInfoUpdated', updatedUserNotaryInformation: IUserNotaryInformation): void;
		(e: 'deleteNotaryInfo', id: string): void;
	}>();

	// REFS
	const isEditingNotary = ref<boolean>(false);
	const notaryStampPreviewRef = useTemplateRef('notaryStampPreviewRef');
	const notaryStateSetting = ref<INotaryStateSetting>(null);

	const notaryStampImage = ref<string>(props.notaryInfo.notaryStampImage);
	const commissionNumber = ref<string>(props.notaryInfo?.notaryCommissionNumber);
	const commissionNumberConfirm = ref<string>('');
	const expirationDate = ref<string>(props.notaryInfo?.expirationDate);
	const county = ref<string>(props.notaryInfo?.county);
	const state = ref<string>(props.notaryInfo?.state);

	const validations = {
		state: {
			required,
			stateAlreadyExists: (value: string) => {
				if (!isEditingNotary.value) return true; // Skip validation if not editing
				return !props.userNotaryInformation?.some((s: any) => s.state === value && s.id !== props.notaryInfo?.id);
			},
		} as any,
		county: {
			required,
			noNumbers: (value: string) => {
				return !/\d/.test(value);
			},
		} as any,
		commissionNumber: { required } as any,
		commissionNumberConfirm: {
			required,
			sameAs: sameAs(commissionNumber),
		} as any,
		expirationDate: {
			required,
			dateInFuture: () => dateInFuture(expirationDate.value, new Date()),
		} as any,
	};

	const v$ = useVuelidate(validations, {
		state,
		county,
		commissionNumber,
		commissionNumberConfirm,
		expirationDate,
	});

	onMounted(() => {
		// Enable editing if the user has some empty fields in their notary information (this should only happen when creating a new notary user setting)
		if (Object.values(props.notaryInfo).some(field => !field)) {
			isEditingNotary.value = true;
		}

		if (state.value) {
			setMyStateNotarySettings();
		}
	});

	// COMPUTED PROPERTIES
	const isNotaryInfoValid = computed(() => {
		return (
			!props.isNotary ||
			(!v$.value.commissionNumber.$invalid &&
				!v$.value.commissionNumberConfirm.$invalid &&
				!v$.value.expirationDate.$invalid &&
				!v$.value.state.$invalid &&
				!v$.value.county.$invalid)
		);
	});
	const notaryActions = computed(() => {
		const actions = [];
		if (isEditingNotary.value) {
			actions.push({
				text: 'Cancel',
				onClick: () => resetNotaryInfo(),
			});
		}
		if (!isEditingNotary.value) {
			actions.push({
				text: 'Remove',
				class: 'button-unwind',
				onClick: () => {
					deleteNotaryInfo();
				},
			});
		}
		actions.push({
			text: isEditingNotary.value ? 'Apply Changes' : 'Edit',
			onClick: () => {
				isEditingNotary.value = !isEditingNotary.value;
				commissionNumberConfirm.value = null;
				if (!isEditingNotary.value) {
					setNotaryInfo();
				}

				// trigger the notary stamp to draw the stamp image
				if (!isEditingNotary.value && notaryStampPreviewRef.value) {
					// pass true to drawNotaryStamp for an image to be generated
					notaryStampPreviewRef.value.drawNotaryStamp(true);
				}
			},
			class: isEditingNotary.value ? 'button-success' : '',
			disabled: isEditingNotary.value && !isNotaryInfoValid.value,
		});
		return actions;
	});

	function stateDropDownChangeHandler() {
		setMyStateNotarySettings();
	}

	function setMyStateNotarySettings() {
		notaryStateSetting.value = props?.notarySettings?.find((ns: INotaryStateSetting) => ns.state === state.value);
	}

	function saveNotaryStampImg(notaryStampImgBase64: string) {
		notaryStampImage.value = notaryStampImgBase64;
	}

	function setNotaryInfo() {
		return new Promise((resolve, reject): void => {
			let updatedUserNotaryInformation;
			$modal.open(modalInfo, {
				name: 'modalInfo',
				passedData: {
					title: 'Notary Information Confirmation',
					info: 'Please review the information you have provided carefully. By submitting this information, you confirm that it is accurate and complete to the best of your knowledge. Providing false information can result in legal actions, including charges of perjury.',
					additionalInfo: props.isNotary
						? `Are you licensed as a Notary?\t Yes\nNotary Commission Number:\t ${commissionNumber.value}\nExpiration Date:\t ${expirationDate.value}\nCounty:\t ${county.value}\nState:\t ${state.value}`
						: 'Are you licensed as a Notary?\t No',
					acceptText: 'I Confirm the Information Provided is Correct',
					cancelText: 'Cancel',
					cancelFunction: () => {
						resetNotaryInfo();
						reject();
					},
				},
				postFunction: () => {
					updatedUserNotaryInformation = {
						id: props.notaryInfo?.id,
						notaryCommissionNumber: commissionNumber.value.toUpperCase(),
						expirationDate: expirationDate.value,
						state: state.value,
						county: county.value.toUpperCase(),
						notaryStampImage: notaryStampImage.value,
					};
					resolve(emit('notaryInfoUpdated', updatedUserNotaryInformation));
				},
				backdrop: false,
			});
		});
	}

	function deleteNotaryInfo() {
		return new Promise((resolve, reject): void => {
			$modal.open(modalInfo, {
				name: 'modalInfo',
				passedData: {
					title: 'Delete Notary Information',
					info: `Are you sure you want to delete your notary information for ${state.value}?`,
					acceptText: 'Yes',
					cancelText: 'Cancel',
					cancelFunction: () => {
						reject();
					},
				},
				postFunction: () => {
					resolve(emit('deleteNotaryInfo', props.notaryInfo.id));
				},
				backdrop: false,
			});
		});
	}

	function resetNotaryInfo() {
		commissionNumber.value = props.notaryInfo?.notaryCommissionNumber;
		commissionNumberConfirm.value = null;
		expirationDate.value = props.notaryInfo?.expirationDate;
		county.value = props.notaryInfo?.county;
		state.value = props.notaryInfo?.state;
		isEditingNotary.value = false;
	}
</script>
<style>
	.notary-state-info-panel {
		margin-bottom: 1.2rem;
	}
	#notary-state-fields {
		display: flex;
		flex-direction: column;
		gap: 1.2rem;
	}

	#notary-state-fieldset {
		display: flex;
		flex-direction: column;
		gap: 1.2rem;
	}
</style>
